import { ENV_VARS } from '@/env';
import { Col, Popconfirm, Row } from 'antd';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as PencilIcon } from '../../../../assets/icons/pencil-white.svg';
import { setPageBlocker } from '../../../../redux/common/CommonActions';
import useShipmentEditPermission from '../../../../redux/hooks/useShipmentEditPermission';
import Button from '../../../components/Buttons/Button';
import IconButton from '../../../components/Buttons/IconButton';
import SvgConverter from '../../../components/Common/SvgConverter';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';
const buttonClassName = isHalliburton ? 'hb-button' : '';

const SaveCancelButtons = ({
  lightMode,
  editMode = true,
  shipmentEditStatus = false,
  onCancel = () => {},
  handleRemove = () => {},
  setEditMode = () => {},
  onSave = false,
  hideDeleteButton = false,
  disableCancelOnHiddenDelete = true,
  isNew = false,
  cancelButtonText = t('translation:default.buttons.cancel'),
  idSuffix,
  isModified = false,
  isDeleteLoading = false,
  hideEditButton = false,
  saveButtonType = 'submit',
  disabled = false,
  isUseBlocker = true,
}) => {
  const hasEditPermission = useShipmentEditPermission();
  const dispatch = useDispatch();

  useEffect(() => {
    isUseBlocker && dispatch(setPageBlocker(isModified));
  }, [isModified]);

  return (
    <>
      {editMode ? (
        <Row gutter={16} className="flex-nowrap" data-testid="save-cancel-buttons-edit-mode">
          <Col>
            <Button
              disabled={disabled}
              type="primary"
              variant="success"
              buttonType={saveButtonType}
              lightMode={lightMode}
              {...(onSave ? { onClick: onSave } : {})}
              id={`add-save-${idSuffix}`}
              className={buttonClassName}
            >
              {isNew ? t('translation:default.buttons.add') : t('translation:default.buttons.save')}
            </Button>
          </Col>
          <Col>
            {isModified ? (
              <Popconfirm
                placement="topRight"
                title={t('translation:default.fields.changesWillBeLost')}
                onConfirm={onCancel}
                okText={t('translation:default.fields.modal.okText')}
                cancelText={t('translation:default.options.no')}
                overlayClassName="delete-popup-container"
              >
                <Button
                  type="default"
                  variant="danger"
                  buttonType="button"
                  lightMode={lightMode}
                  id={`cancel-${idSuffix}`}
                  className={buttonClassName}
                >
                  {cancelButtonText}
                </Button>
              </Popconfirm>
            ) : (
              <Button
                type="default"
                variant="danger"
                buttonType="button"
                lightMode={lightMode}
                id={`cancel-${idSuffix}`}
                onClick={onCancel}
                disabled={(hideDeleteButton && disableCancelOnHiddenDelete) || disabled}
                className={buttonClassName}
              >
                {cancelButtonText}
              </Button>
            )}
          </Col>
        </Row>
      ) : (
        <Row className="flex-nowrap" data-testid="save-cancel-buttons-no-edit-mode">
          {!hideEditButton ? (
            <Col className={`${!hideDeleteButton ? 'mr-16' : ''} lh-0`}>
              <IconButton
                onClick={() => setEditMode(true)}
                disabled={shipmentEditStatus || !hasEditPermission}
                id={`edit-${idSuffix}`}
              >
                <SvgConverter
                  disabled={shipmentEditStatus || !hasEditPermission}
                  Icon={PencilIcon}
                  color="header"
                  className={isHalliburton ? 'hb-dark-icon' : ''}
                />
              </IconButton>
            </Col>
          ) : (
            <></>
          )}

          {!hideDeleteButton && (
            <Col className="lh-0">
              <Popconfirm
                placement="topRight"
                title={t('translation:default.popConfirm.title')}
                onConfirm={handleRemove}
                okText={t('translation:default.buttons.delete')}
                cancelText={t('translation:default.options.no')}
                overlayClassName="delete-popup-container"
                okType="danger"
              >
                <IconButton
                  disabled={shipmentEditStatus || !hasEditPermission}
                  id={`delete-${idSuffix}`}
                  loading={isDeleteLoading}
                  dataTestId="delete-button"
                >
                  <SvgConverter
                    disabled={shipmentEditStatus || !hasEditPermission}
                    Icon={DeleteIcon}
                    color="iconDanger"
                  />
                </IconButton>
              </Popconfirm>
            </Col>
          )}
        </Row>
      )}
    </>
  );
};

export default SaveCancelButtons;
